import { FC, useEffect, useState, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { getStorage, setStorage } from "@/utils/storage";
import { ConfigProvider, notification, App } from "antd";
import {
  settings,
  UserContext,
  UserSettings
} from "@/layout/context/SettingsContext";
import { User, UserLangEnum, UserSettingEnum } from "@/types/user";
import { LANGS } from "./HomeLayout/components/Locale";
import profileLogout from "@/pages/Profile/utils/logout";
import { useCheckStatus } from "./hooks/useCheckStatus";
import { useTranslation } from "react-i18next";

import "dayjs/locale/zh-cn";
import "dayjs/locale/zh-tw";
import "dayjs/locale/en";
import "dayjs/locale/vi";
import "dayjs/locale/ko";
import dayjs from "dayjs";
import { useCallback } from "react";
import useDefaultPage from "./hooks/useDefaultPage";
import { userFetch } from "@/apis/user";
import { DictContext } from "./context/DictContext";
import useGetBasicInfo from "./hooks/useGetBasicInfo";
import {
  handleGWPMessage,
  isValidGWPLoginData
} from "@/utils/gwpAutoLogin";
import { SLoading } from "@/styles/SLoading";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/store/index";
import { setCurUser, setLoginStatus } from "@/store/modules/userReducer";
import { setCurOrg } from "@/store/modules/orgReducer";
import i18n from "@/locale";
import { ROUTER_KEYS } from "@/router/routers";
import { hasPermission } from "@/utils/user";
import { UserPermissionEnum } from "@/types/user";
import { orgFetchs } from "@/apis/org";
import { Org } from "@/types/org";

const IndexHome: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const [{ checkStatus }] = useCheckStatus();
  useDefaultPage();

  const [userSettings, setUserSettings] = useState<UserSettings>(settings);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [autoLoginLoading, setAutoLoginLoading] = useState<boolean>(false); // GWP 自动登录中的加载标记
  const { getInfo, ...basicInfo } = useGetBasicInfo();

  // 使用 ref 代替局部变量避免 useEffect 重置
  const isTipRef = useState<{ current: boolean }>({ current: false })[0];

  // 计算是否来自允许的 GWP 来源（用于决定是否保留首屏遮罩）
  const isFromGWPRef = useRef<boolean>(false);
  useEffect(() => {
    try {
      const referrer = document.referrer;
      // 如果有referrer，说明是从外部跳转过来的
      isFromGWPRef.current = !!referrer;
    } catch {
      isFromGWPRef.current = false;
    }
    // 如果已有 token 或非 GWP 跳转，立即移除首屏遮罩，避免挡住登录页
    const hasToken = !!(getStorage("SUPPLIER_ID") || getStorage("accessToken"));
    if (hasToken || !isFromGWPRef.current) {
      (window as any).__hideSplash?.();
    } else {
      // 兜底：最长保留 12 秒，避免极端情况下遮罩不消失
      const tid = setTimeout(() => (window as any).__hideSplash?.(), 12000);
      return () => clearTimeout(tid);
    }
  }, []);

  const getCurrUserInfo = useCallback(
    (settings: UserSettings) => {
      const lang = getValue<UserLangEnum>(settings, UserSettingEnum.LANG);

      if (lang === UserLangEnum.EN) {
        dayjs.locale("en");
      } else if (lang === UserLangEnum.TR) {
        dayjs.locale("zh-tw");
      } else if (lang === UserLangEnum.ZH) {
        dayjs.locale("zh-cn");
      } else if (lang === UserLangEnum.VN) {
        dayjs.locale("vi");
      } else if (lang === UserLangEnum.KO) {
        dayjs.locale("ko");
      }

      setUserSettings([
        {
          type: UserSettingEnum.LANG,
          value: lang
        },
        {
          type: UserSettingEnum.THEME,
          value: getValue<string>(settings, UserSettingEnum.THEME)
        }
      ]);
    },
    [setUserSettings]
  );

  useEffect(() => {
    const token = getStorage("SUPPLIER_ID") || getStorage("accessToken") || "";
    if (token) {
      userFetch()
        .then(user => {
          setLoading(false);
          setUser(user);
          setUserSettings(user.settings);
          getCurrUserInfo(user.settings);
          // 已登录场景，确保移除首屏遮罩
          (window as any).__hideSplash?.();
        })
        .catch(err => {
          // console.log(err)
        });
    } else {
      setLoading(false);
      // 无 token 且非 GWP 跳转时，确保移除首屏遮罩
      if (!isFromGWPRef.current) {
        (window as any).__hideSplash?.();
      }
    }
    // 首次挂载获取用户设置即可
    // getCurrUserInfo 内部只使用传入的 settings，不会在未变化时产生副作用
  }, [getCurrUserInfo]);

  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    getInfo();
    if (getStorage("accessToken")) {
      checkStatus().then(resStatus => {
        if (!resStatus) {
          if (isTipRef.current) {
            isTipRef.current = false;
          } else {
            isTipRef.current = true;
            notification.warning({ placement: 'top', message: t("layout.userAbnormalTip") });
            setTimeout(() => {
              profileLogout();
            }, 1500);
          }
        }
      });
    }
    // 仅在首次挂载时检查状态与获取基础字典，避免因 getInfo 引用变化造成重复请求
  }, [checkStatus, getInfo, t, isTipRef]);

  // GWP 自动登录：监听来自 GWP 的 postMessage
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // 仅验证数据格式，移除来源验证（因为来源域名太多无法枚举）
      const isDataValid = isValidGWPLoginData(event.data);
      if (!isDataValid) return;
      // 切换到应用内遮罩，并移除首屏遮罩
      setAutoLoginLoading(true);
      (window as any).__hideSplash?.();
      await handleGWPMessage(
        event,
        // 成功回调：保存用户信息并跳转
        async (user: User) => {
          setUser(user);
          setUserSettings(user.settings);
          dispatch(setCurUser(user));
          dispatch(setLoginStatus(true));
          setStorage("curUser", user, "locale");

          // 设置用户语言
          const langSetting = user.settings.find(
            setting => setting.type === UserSettingEnum.LANG
          );
          if (langSetting && langSetting.value !== getStorage("locale")) {
            const userLang = LANGS.find(lang => lang.key === langSetting.value);
            if (userLang) {
              setStorage("locale", userLang.key);
              i18n.changeLanguage(userLang.appApply);
            }
          }

          // 设置当前组织 - 对齐手动登录的组织初始化逻辑
          try {
            // 1. 获取组织列表：ADMIN 用户获取所有组织，普通用户使用 user.orgs
            let orgList: Org[] = [];
            const isAdmin = hasPermission(user, [UserPermissionEnum.ADMIN]);

            if (isAdmin) {
              try {
                orgList = (await orgFetchs()).body;
              } catch (error) {
                console.error("[GWP Auto Login] 获取组织列表失败:", error);
                orgList = user.orgs || [];
              }
            } else {
              orgList = user.orgs || [];
            }

            if (orgList.length === 0) {
              console.warn("[GWP Auto Login] 用户没有组织，跳过组织设置");
              setAutoLoginLoading(false);
              navigate(ROUTER_KEYS.WELCOME);
              return;
            }

            // 2. 尝试从 localStorage 读取上次登录的组织 ID
            let accountStorage = getStorage("account") || [];
            let targetOrg: Org | null = null;
            let foundAccountItem: any = null;

            if (accountStorage.length > 0) {
              foundAccountItem = accountStorage.find(
                (item: any) => item.mobile === user.mobile
              );

              if (foundAccountItem?.loginOrgId) {
                // 在组织列表中查找上次登录的组织
                targetOrg = orgList.find(org => org.id === foundAccountItem.loginOrgId) || null;
              }
            }

            // 3. 如果没有找到上次登录的组织，使用第一个组织
            if (!targetOrg) {
              targetOrg = orgList[0];
            }

            // 4. 设置当前组织
            dispatch(setCurOrg(targetOrg));
            setStorage("curOrgId", targetOrg.id, "locale");

            // 5. 更新或创建 account storage（对齐手动登录逻辑）
            if (foundAccountItem) {
              // 更新现有用户的 loginOrgId
              foundAccountItem.loginOrgId = targetOrg.id;
            } else {
              // 添加新用户到 account
              accountStorage.push({
                mobile: user.mobile,
                areaCode: user.areaCode,
                loginOrgId: targetOrg.id
              });
            }
            setStorage("account", accountStorage, "locale");

          } catch (error) {
            console.error("[GWP Auto Login] 组织初始化失败:", error);
          }

          // 跳转到欢迎页面
          navigate(ROUTER_KEYS.WELCOME);
          setAutoLoginLoading(false);
        },
        // 错误回调：记录错误
        (error: any) => {
          console.error("[GWP Auto Login] 处理登录失败:", error);
          setAutoLoginLoading(false);
          // 登录失败也移除首屏遮罩，避免长时间遮挡
          (window as any).__hideSplash?.();
        }
      );
    };

    // 添加事件监听器
    window.addEventListener("message", handleMessage);

    // 清理监听器
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dispatch, navigate, t]);

  const getValue = <T,>(settings: UserSettings, type: UserSettingEnum): T => {
    return settings?.find(setting => setting.type === type)?.value as T;
  };

  if (loading) {
    return <div>Loading user...</div>;
  }

  return (
    <UserContext.Provider
      value={[
        [user, setUser],
        [userSettings, setUserSettings]
      ]}
    >
      <DictContext.Provider value={basicInfo}>
        <ConfigProvider
          theme={{
            token: {
              colorPrimary:
                userSettings.find(item => item.type === UserSettingEnum.THEME)
                  ?.value || getValue<string>(settings, UserSettingEnum.THEME),
              fontFamily: 'var(--app-font-family)', // 全局字体设置
            },
            components: {
              // 全局表格列间距
              Table: {
                cellPaddingBlockSM: 0,
                cellPaddingInlineSM: 1
              }
            }
          }}
          locale={
            LANGS.find(
              lang =>
                lang.key === (
                  getValue<UserLangEnum>(userSettings, UserSettingEnum.LANG) ||
                  getValue<UserLangEnum>(settings, UserSettingEnum.LANG)
                )
            )?.apply
          }
          componentSize="small"
        >
          <App>
            {autoLoginLoading && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(255,255,255,0.65)",
                  zIndex: 9999,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(2px)"
                }}
              >
                <SLoading />
                <div style={{ marginTop: 12, color: "#333", fontSize: 14 }}>
                  正在从 GWP 自动登录，请稍候...
                </div>
              </div>
            )}
            <Outlet />
          </App>
        </ConfigProvider>
      </DictContext.Provider>
    </UserContext.Provider>
  );
};

export default IndexHome;
